import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearError, sendOtp } from '../store/slices/authSlice';
import { AurieqLogo } from '../components/Brand/AurieqLogo';
import AuthPageShell from '../components/Auth/AuthPageShell';
import { BRAND } from '../constants/branding';
import { authCardSx, authFieldSx, gradientButtonSx } from '../constants/brandStyles';

const registerSchema = yup.object({
  username: yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required').min(8, 'Password must be at least 8 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
});

interface RegisterFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema),
    mode: 'onChange',
  });

  React.useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const { confirmPassword, ...registerData } = data;
      await dispatch(sendOtp({ email: registerData.email, purpose: 'register' })).unwrap();
      navigate('/verify-email', {
        state: {
          purpose: 'register',
          email: registerData.email,
          username: registerData.username,
          password: registerData.password,
        },
      });
    } catch (err) {
      console.error('Registration failed:', err);
    }
  };

  return (
    <AuthPageShell>
      <Box sx={authCardSx}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <AurieqLogo variant="full" height={72} to="/" />
        </Box>
        <Typography variant="h5" align="center" gutterBottom sx={{ color: BRAND.colors.text }}>
          Create your account
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            {...register('username')}
            fullWidth
            label="Username"
            error={!!errors.username}
            helperText={errors.username?.message}
            margin="normal"
            autoComplete="username"
            autoFocus
            sx={authFieldSx}
          />
          <TextField
            {...register('email')}
            fullWidth
            label="Email Address"
            type="email"
            error={!!errors.email}
            helperText={errors.email?.message}
            margin="normal"
            autoComplete="email"
            sx={authFieldSx}
          />
          <TextField
            {...register('password')}
            fullWidth
            label="Password"
            type="password"
            error={!!errors.password}
            helperText={errors.password?.message}
            margin="normal"
            autoComplete="new-password"
            sx={authFieldSx}
          />
          <TextField
            {...register('confirmPassword')}
            fullWidth
            label="Confirm Password"
            type="password"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            margin="normal"
            autoComplete="new-password"
            sx={authFieldSx}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ ...gradientButtonSx, mt: 3, mb: 2 }}
            disabled={!isValid || loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue with email verification'}
          </Button>

          <Divider sx={{ my: 2, borderColor: BRAND.colors.border }} />
          <Box textAlign="center">
            <Link component={RouterLink} to="/login" sx={{ color: BRAND.colors.primary }}>
              Already have an account? Sign In
            </Link>
          </Box>
          <Typography variant="caption" align="center" display="block" sx={{ mt: 2, color: BRAND.colors.textDim }}>
            <Link component={RouterLink} to="/privacy" sx={{ color: 'inherit' }}>Privacy</Link>
            {' · '}
            <Link component={RouterLink} to="/terms" sx={{ color: 'inherit' }}>Terms</Link>
          </Typography>
        </Box>
      </Box>
    </AuthPageShell>
  );
};

export default RegisterPage;
